/**
 * PasteSkuListDialog: set many SKUs to Skip or Always 0 in one go, always
 * through a review first.
 *
 * Paste one SKU or product handle per line (up to 2000), check it with
 * `previewPushSkuRules`, then set. The review lists what matched and, in plain
 * words, every line that was left out and why.
 *
 * A blank's own stock line (`BLANK-...`) and a pre-printed design can't take a
 * rule, so their lines offer the base garment (`baseGarmentOffer`). "Add all N
 * base garments" (or one line's button) checks only those base garments and
 * folds them into the review (`mergePreviews`): the pasted list is never
 * checked again.
 *
 * Always 0 has no second step: with Always 0 picked, the review itself shows
 * the impact (the same words as `AlwaysZeroConfirmDialog`, numbers first) and
 * its button, in the Always 0 tone, commits. Never a dialog on top of this one
 * (DESIGN.md 10). No CSV wizard here on purpose: a paste is a list of codes,
 * not a file with columns.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Ban, Check, Loader2, Plus } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { SegmentedControl } from '@/components/ui/segmented-control';
import { NoteLine } from '@/components/shared/NoteLine';
import { SectionError } from '@/components/shared/SectionError';
import { WarningNotice } from '@/components/shared/WarningNotice';
import { trpc } from '@/lib/trpc';
import { cn } from '@/lib/utils';
import { useSetPushSkuRules } from '../hooks/useSetPushSkuRules';
import {
  PASTE_LINE_LIMIT,
  PREVIEW_VISIBLE_ROWS,
  baseGarmentOffer,
  idsToChange,
  mergePreviews,
  parsePastedCodes,
  summarizeAlwaysZero,
  willPauseThePush,
  type PreviewResult,
  type PushSkuRule,
} from '../utils/push-sku-rules';
import { AlwaysZeroImpact } from './AlwaysZeroConfirmDialog';
import { RuleBadge, SkuKindLabel } from './PushRuleBadge';
import { ALWAYS_ZERO_BUTTON_CLASS } from './push-rule-styles';

interface PasteSkuListDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Bumped by the caller on every open, so each open starts clean. */
  session: number;
  /** The SAVED Always 0 limit, 0 = No limit (the big-batch pause reads its ceiling off it). */
  alwaysZeroLimit: number;
}

type Step = 'edit' | 'review';

const PRIMARY_CLASS = 'bg-threadheads-purple text-white hover:bg-threadheads-purple/90';
const MATCHED_HEADING_ID = 'push-rules-matched-heading';

export function PasteSkuListDialog({ open, onOpenChange, session, alwaysZeroLimit }: PasteSkuListDialogProps) {
  // The body holds every step's state; keying it by the open session resets
  // the text, the preview and the step without an effect.
  const [busy, setBusy] = useState(false);
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // A write in flight keeps the dialog up, so it never closes over an
        // unresolved change.
        if (!next && busy) return;
        onOpenChange(next);
      }}
    >
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
        {/* Radix unmounts the content after its exit animation, so the body
            leaves with it; the key gives every open a fresh body. */}
        <PasteSkuListBody
          key={session}
          alwaysZeroLimit={alwaysZeroLimit}
          onBusyChange={setBusy}
          onDone={() => onOpenChange(false)}
        />
      </DialogContent>
    </Dialog>
  );
}

function PasteSkuListBody({
  alwaysZeroLimit,
  onBusyChange,
  onDone,
}: {
  alwaysZeroLimit: number;
  onBusyChange: (busy: boolean) => void;
  onDone: () => void;
}) {
  const { t } = useTranslation('inventory');
  const [step, setStep] = useState<Step>('edit');
  const [text, setText] = useState('');
  // Skip first: the choice that can never zero a storefront.
  const [rule, setRule] = useState<PushSkuRule>('skip');
  const [checkedCodes, setCheckedCodes] = useState<string[]>([]);
  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const [previewFailed, setPreviewFailed] = useState(false);
  /** The base garment add in flight: one line's id, or 'all'. */
  const [addingBase, setAddingBase] = useState<string | null>(null);
  const [baseAddFailed, setBaseAddFailed] = useState(false);
  const previewMutation = trpc.stockSync.previewPushSkuRules.useMutation();
  const { apply, isPending: applying } = useSetPushSkuRules();
  const requestRef = useRef(0);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const matchedHeadingRef = useRef<HTMLHeadingElement>(null);
  const textareaId = 'push-rules-paste-text';
  const pasteNoteId = 'push-rules-paste-note';

  const parsed = useMemo(() => parsePastedCodes(text), [text]);
  const checking = previewMutation.isPending && addingBase === null;

  useEffect(() => {
    onBusyChange(applying);
  }, [applying, onBusyChange]);

  const runPreview = async (codes: string[]) => {
    const request = ++requestRef.current;
    setPreviewFailed(false);
    setBaseAddFailed(false);
    setCheckedCodes(codes);
    try {
      const result = await previewMutation.mutateAsync({ codes });
      // A newer check (Retry) superseded this one.
      if (request !== requestRef.current) return;
      setPreview(result);
      setStep('review');
    } catch {
      if (request !== requestRef.current) return;
      setPreview(null);
      setPreviewFailed(true);
      setStep('review');
    }
  };

  // The footer swaps on every step, so the button that was pressed is gone;
  // focus lands on the step's heading instead of falling to the page.
  useEffect(() => {
    if (step !== 'edit') headingRef.current?.focus();
  }, [step]);

  const check = () => {
    if (parsed.codes.length === 0 || parsed.overLimit || checking) return;
    void runPreview(parsed.codes);
  };

  const matchedIds = useMemo(() => new Set(preview?.matched.map((m) => m.id) ?? []), [preview]);
  // Base garments offered by left-out lines (a blank's stock line, a
  // pre-printed design) and not in the review yet, once each.
  const baseOffers = useMemo(() => {
    const ids = new Set<string>();
    for (const line of preview?.unmatched ?? []) {
      const offer = baseGarmentOffer(line);
      if (offer && !matchedIds.has(offer)) ids.add(offer);
    }
    return [...ids];
  }, [preview, matchedIds]);

  /** Check only these base garments and fold them into the review. */
  const addBaseGarments = async (skuIds: string[], marker: string) => {
    if (skuIds.length === 0 || addingBase !== null) return;
    const request = requestRef.current;
    setAddingBase(marker);
    setBaseAddFailed(false);
    try {
      const result = await previewMutation.mutateAsync({ skuIds });
      if (request !== requestRef.current) return;
      setPreview((prev) => (prev ? mergePreviews(prev, result) : prev));
      // The pressed button is now "Added" (disabled); the count it changed is here.
      matchedHeadingRef.current?.focus();
    } catch {
      if (request === requestRef.current) setBaseAddFailed(true);
    } finally {
      setAddingBase(null);
    }
  };

  const changeIds = preview ? idsToChange(preview.matched, rule) : [];
  const sameCount = preview ? preview.matched.length - changeIds.length : 0;
  const zero = rule === 'always_zero';
  const summary = useMemo(() => (preview ? summarizeAlwaysZero(preview.matched) : null), [preview]);

  const commit = async () => {
    if (changeIds.length === 0) return;
    const ok = await apply(changeIds, rule);
    if (ok) onDone();
  };

  const ruleLabel = zero ? t('stockSync.pushRules.rule.alwaysZero') : t('stockSync.pushRules.rule.skip');

  // ── Edit ────────────────────────────────────────────────────────────────
  if (step === 'edit') {
    const countNote = parsed.overLimit
      ? t('stockSync.pushRules.paste.overLimit', { count: parsed.codes.length, limit: PASTE_LINE_LIMIT.toLocaleString() })
      : parsed.codes.length > 0
        ? t('stockSync.pushRules.paste.entries', { count: parsed.codes.length })
        : t('stockSync.pushRules.paste.hint', { limit: PASTE_LINE_LIMIT.toLocaleString() });
    return (
      <>
        <DialogHeader>
          <DialogTitle>{t('stockSync.pushRules.paste.title')}</DialogTitle>
          <DialogDescription>{t('stockSync.pushRules.paste.description')}</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor={textareaId}>{t('stockSync.pushRules.paste.label')}</Label>
          <Textarea
            id={textareaId}
            value={text}
            onChange={(event) => setText(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
                event.preventDefault();
                check();
              }
            }}
            rows={10}
            spellCheck={false}
            autoComplete="off"
            aria-invalid={parsed.overLimit || undefined}
            aria-describedby={pasteNoteId}
            placeholder={'ATEE-S-BK\nclassic-tee'}
            className={cn('font-mono text-sm', parsed.overLimit && 'border-destructive')}
          />
          {/* One slot for the hint, the count and the error, so the layout
              never moves while typing (DESIGN.md 15.3). */}
          <div className="min-h-4 space-y-0.5">
            <NoteLine id={pasteNoteId} tone={parsed.overLimit ? 'error' : 'muted'}>
              {countNote}
            </NoteLine>
            {parsed.duplicates > 0 && !parsed.overLimit && (
              <NoteLine>{t('stockSync.pushRules.paste.duplicates', { count: parsed.duplicates })}</NoteLine>
            )}
            {parsed.tooLong > 0 && (
              <NoteLine tone="warning">{t('stockSync.pushRules.paste.tooLong', { count: parsed.tooLong })}</NoteLine>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onDone}>
            {t('stockSync.pushRules.cancel')}
          </Button>
          <Button
            type="button"
            className={PRIMARY_CLASS}
            disabled={parsed.codes.length === 0 || parsed.overLimit || checking}
            aria-busy={checking || undefined}
            onClick={check}
          >
            {checking && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin motion-reduce:animate-none" aria-hidden="true" />}
            {t('stockSync.pushRules.paste.check')}
          </Button>
        </DialogFooter>
      </>
    );
  }

  // ── Review (and, for Always 0, the confirm) ─────────────────────────────
  const visible = preview ? preview.matched.slice(0, PREVIEW_VISIBLE_ROWS) : [];
  const hiddenCount = preview ? preview.matched.length - visible.length : 0;

  return (
    <>
      <DialogHeader>
        <DialogTitle ref={headingRef} tabIndex={-1} className="outline-none">
          {t('stockSync.pushRules.paste.reviewTitle')}
        </DialogTitle>
        <DialogDescription>
          {t('stockSync.pushRules.paste.reviewDescription', { count: checkedCodes.length })}
        </DialogDescription>
      </DialogHeader>

      {checking && !preview && (
        <div role="status" className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />
          {t('stockSync.pushRules.paste.checking')}
        </div>
      )}

      {previewFailed && !checking && (
        <SectionError
          variant="compact"
          title={t('stockSync.pushRules.paste.previewErrorTitle')}
          body={t('stockSync.pushRules.paste.previewErrorBody')}
          retryLabel={t('stockSync.pushRules.retry')}
          onRetry={() => void runPreview(checkedCodes)}
        />
      )}

      {preview && (
        <div className={cn('space-y-4', checking && 'opacity-60')} aria-busy={checking || undefined}>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <span className="text-sm font-medium">{t('stockSync.pushRules.setTo')}</span>
            <SegmentedControl<PushSkuRule>
              ariaLabel={t('stockSync.pushRules.setTo')}
              value={rule}
              onChange={setRule}
              options={[
                { value: 'skip', label: t('stockSync.pushRules.rule.skip') },
                { value: 'always_zero', label: t('stockSync.pushRules.rule.alwaysZero') },
              ]}
            />
            <span className="text-xs text-foreground">
              {zero ? t('stockSync.pushRules.rule.alwaysZeroMeaning') : t('stockSync.pushRules.rule.skipMeaning')}
            </span>
          </div>

          {/* With Always 0 picked, this IS the confirm: the impact, numbers
              first, read before the button below. */}
          {zero && changeIds.length > 0 && (
            <div className="rounded-md border border-amber-300 p-3">
              <AlwaysZeroImpact
                summary={summary}
                previewFailed={false}
                willPause={willPauseThePush(preview, alwaysZeroLimit)}
              />
            </div>
          )}

          {preview.truncated && (
            <WarningNotice>{t('stockSync.pushRules.paste.truncated', { cap: preview.matched.length.toLocaleString() })}</WarningNotice>
          )}

          <section aria-labelledby={MATCHED_HEADING_ID} className="space-y-2">
            <h3
              ref={matchedHeadingRef}
              id={MATCHED_HEADING_ID}
              tabIndex={-1}
              className="rounded-sm text-sm font-medium outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
            >
              {t('stockSync.pushRules.paste.found', { count: preview.matched.length })}
              {sameCount > 0 && (
                <span className="ml-2 font-normal text-muted-foreground">
                  {t('stockSync.pushRules.paste.alreadySet', { count: sameCount, rule: ruleLabel })}
                </span>
              )}
            </h3>
            {preview.matched.length > 0 ? (
              <ul className="max-h-64 divide-y overflow-y-auto rounded-md border">
                {visible.map((row) => (
                  <li key={row.id} className="flex flex-wrap items-center gap-x-2 gap-y-1 px-3 py-1.5 text-sm">
                    <span className="min-w-0 break-all font-mono text-xs">{row.sku ?? '-'}</span>
                    <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground" title={row.title ?? row.productTitle ?? undefined}>
                      {row.title ?? row.productTitle ?? ''}
                      {row.expandedFromHandle && (
                        <> {t('stockSync.pushRules.paste.fromHandle', { handle: row.expandedFromHandle })}</>
                      )}
                    </span>
                    <SkuKindLabel productionType={row.productionType} isBaseGarment={row.isBaseGarment} />
                    {row.currentRule && (
                      <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
                        {row.currentRule === rule
                          ? t('stockSync.pushRules.paste.already')
                          : t('stockSync.pushRules.paste.currently')}
                        <RuleBadge rule={row.currentRule} />
                      </span>
                    )}
                  </li>
                ))}
                {hiddenCount > 0 && (
                  <li className="px-3 py-1.5 text-xs text-muted-foreground">
                    {t('stockSync.pushRules.paste.more', { count: hiddenCount })}
                  </li>
                )}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">{t('stockSync.pushRules.paste.noneFound')}</p>
            )}
          </section>

          {preview.unmatched.length > 0 && (
            <section aria-labelledby="push-rules-unmatched-heading" className="space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 id="push-rules-unmatched-heading" className="text-sm font-medium">
                  {t('stockSync.pushRules.paste.notAdded', { count: preview.unmatched.length })}
                </h3>
                {baseOffers.length > 1 && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-7 gap-1 px-2 text-xs"
                    disabled={addingBase !== null || checking || applying}
                    aria-busy={addingBase === 'all' || undefined}
                    onClick={() => void addBaseGarments(baseOffers, 'all')}
                  >
                    {addingBase === 'all' ? (
                      <Loader2 className="h-3 w-3 animate-spin motion-reduce:animate-none" aria-hidden="true" />
                    ) : (
                      <Plus className="h-3 w-3" aria-hidden="true" />
                    )}
                    {t('stockSync.pushRules.paste.addAllBaseGarments', { count: baseOffers.length })}
                  </Button>
                )}
              </div>
              {baseAddFailed && (
                <div role="alert">
                  <NoteLine tone="error">{t('stockSync.pushRules.paste.addBaseFailed')}</NoteLine>
                </div>
              )}
              <ul className="max-h-48 divide-y overflow-y-auto rounded-md border">
                {preview.unmatched.slice(0, PREVIEW_VISIBLE_ROWS).map((line, index) => {
                  const baseId = baseGarmentOffer(line);
                  const added = baseId !== null && matchedIds.has(baseId);
                  return (
                    <li
                      key={`${line.code ?? 'none'}-${index}`}
                      className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-1.5"
                    >
                      <span className="min-w-0 break-all font-mono text-xs">{line.code ?? '-'}</span>
                      <span className="min-w-0 flex-1 basis-48 text-xs text-muted-foreground">
                        {t(`stockSync.pushRules.reason.${line.reason}`)}
                      </span>
                      {baseId !== null && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-7 gap-1 px-2 text-xs"
                          disabled={added || addingBase !== null || checking || applying}
                          aria-busy={addingBase === baseId || undefined}
                          onClick={() => void addBaseGarments([baseId], baseId)}
                        >
                          {addingBase === baseId ? (
                            <Loader2 className="h-3 w-3 animate-spin motion-reduce:animate-none" aria-hidden="true" />
                          ) : added ? (
                            <Check className="h-3 w-3" aria-hidden="true" />
                          ) : (
                            <Plus className="h-3 w-3" aria-hidden="true" />
                          )}
                          {added ? t('stockSync.pushRules.paste.baseGarmentAdded') : t('stockSync.pushRules.paste.addBaseGarment')}
                          {/* The visible words lead the name (WCAG 2.5.3); the code tells the rows apart. */}
                          {line.code && <span className="sr-only"> {line.code}</span>}
                        </Button>
                      )}
                    </li>
                  );
                })}
                {preview.unmatched.length > PREVIEW_VISIBLE_ROWS && (
                  <li className="px-3 py-1.5 text-xs text-muted-foreground">
                    {t('stockSync.pushRules.paste.more', { count: preview.unmatched.length - PREVIEW_VISIBLE_ROWS })}
                  </li>
                )}
              </ul>
            </section>
          )}
        </div>
      )}

      <DialogFooter>
        <Button
          type="button"
          variant="outline"
          disabled={applying || checking || addingBase !== null}
          onClick={() => setStep('edit')}
        >
          {t('stockSync.pushRules.back')}
        </Button>
        <Button type="button" variant="outline" disabled={applying} onClick={onDone}>
          {t('stockSync.pushRules.cancel')}
        </Button>
        <Button
          type="button"
          variant={zero ? 'outline' : 'default'}
          className={cn('gap-1.5', zero ? ALWAYS_ZERO_BUTTON_CLASS : PRIMARY_CLASS)}
          disabled={!preview || checking || applying || addingBase !== null || changeIds.length === 0}
          aria-busy={applying || undefined}
          onClick={() => void commit()}
        >
          {applying ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" aria-hidden="true" />
          ) : (
            zero && changeIds.length > 0 && <Ban className="h-3.5 w-3.5" aria-hidden="true" />
          )}
          {changeIds.length === 0 && preview
            ? t('stockSync.pushRules.paste.nothingToChange')
            : t('stockSync.pushRules.paste.apply', { count: changeIds.length, rule: ruleLabel })}
        </Button>
      </DialogFooter>
    </>
  );
}
